import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useRepoContext, type DriveDocType, type RepoSummary } from '../contexts/RepoContext';
import { FolderPickerModal } from './FolderPickerModal';
import { AddRepoModal } from './AddRepoModal';

interface RepoFull extends RepoSummary {
    defaultIssueLabels?: string[];
    status?: 'active' | 'archived';
    indexPaths?: string[];
    indexExcludePaths?: string[];
}

// Filter labels are origin-based — see AddRepoModal for the rationale.
const DRIVE_DOC_TYPES: Array<{ key: DriveDocType; label: string }> = [
    { key: 'docs', label: 'Google Docs (native)' },
    { key: 'sheets', label: 'Google Sheets' },
    { key: 'slides', label: 'Google Slides' },
    { key: 'pdf', label: 'PDFs' },
    { key: 'text', label: 'Text & Markdown files' }
];

type PickerTarget =
    | { kind: 'discover'; repoId: string; fullName: string; includes: string[]; excludes: string[] }
    | { kind: 'edit'; repo: RepoFull };

interface DiscoveredRepo {
    owner: string;
    name: string;
    repoId: string;
    fullName: string;
    defaultBranch: string;
    private: boolean;
    description: string | null;
    pushedAt: string | null;
    registered: boolean;
}

interface DiscoverResponse {
    org: { name: string; installationId: number };
    repos: DiscoveredRepo[];
    githubInstallUrl: string;
}

export function AdminReposPage() {
    const { allowedRepos, refresh } = useRepoContext();
    const [expanded, setExpanded] = useState<string | null>(null);
    const [labelDraft, setLabelDraft] = useState<Record<string, string>>({});
    const [picker, setPicker] = useState<PickerTarget | null>(null);
    const [discoverSelections, setDiscoverSelections] = useState<Record<string, { includes: string[]; excludes: string[] }>>({});
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [discover, setDiscover] = useState<DiscoverResponse | null>(null);
    const [discovering, setDiscovering] = useState(false);
    const [discoverError, setDiscoverError] = useState<string | null>(null);
    const [showDiscover, setShowDiscover] = useState(false);
    const [showAddSource, setShowAddSource] = useState(false);

    const list = (allowedRepos ?? []) as RepoFull[];

    const loadDiscovery = useCallback(async () => {
        setDiscovering(true);
        setDiscoverError(null);
        try {
            const r = await apiCall<DiscoverResponse>('/manage/repos/discover');
            setDiscover(r);
        } catch (err) {
            setDiscoverError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setDiscovering(false);
        }
    }, []);

    useEffect(() => {
        if (showDiscover && !discover && !discovering) {
            void loadDiscovery();
        }
    }, [showDiscover, discover, discovering, loadDiscovery]);

    async function patchRepo(repo: RepoFull, patch: Partial<RepoFull>) {
        setBusy(repo.id);
        setError(null);
        try {
            const [owner, name] = repo.id.split('/');
            await apiCall(`/manage/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`, {
                method: 'PATCH',
                body: patch
            });
            await refresh();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    async function reindex(repo: RepoFull) {
        if (!window.confirm(`Re-run the full-tree indexer on ${repo.id}?\nThis re-enqueues every reviewable file at current HEAD.`)) return;
        setBusy(repo.id);
        setError(null);
        setNotice(null);
        try {
            const [owner, name] = repo.id.split('/');
            const r = await apiCall<{ filesEnqueued: number; status: string }>(
                `/manage/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/reindex`,
                { method: 'POST' }
            );
            setNotice(
                `Re-index started for ${repo.id}: ${r.filesEnqueued} files enqueued. ` +
                `Processing runs asynchronously — wait a minute and refresh the doc to see updated content.`
            );
            await refresh();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    async function removeRepo(repo: RepoFull) {
        if (!window.confirm(
            `Remove ${repo.id} from this tenant?\nThis deletes the registration, all indexed docs, and all user grants on it.\nComments and drawings are preserved for audit history.\nYou can re-add it from Discover afterwards.`
        )) return;
        setBusy(repo.id);
        setError(null);
        try {
            const [owner, name] = repo.id.split('/');
            await apiCall(`/manage/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`, { method: 'DELETE' });
            await refresh();
            await loadDiscovery();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    function saveLabels(repo: RepoFull) {
        const draft = labelDraft[repo.id] ?? (repo.defaultIssueLabels ?? []).join(', ');
        const labels = draft.split(',').map((s) => s.trim()).filter(Boolean);
        void patchRepo(repo, { defaultIssueLabels: labels });
    }

    async function register(d: DiscoveredRepo) {
        setBusy(d.repoId);
        setError(null);
        try {
            const sel = discoverSelections[d.repoId];
            const body: { repoId: string; indexPaths?: string[]; indexExcludePaths?: string[] } = { repoId: d.repoId };
            if (sel?.includes.length) body.indexPaths = sel.includes;
            if (sel?.excludes.length) body.indexExcludePaths = sel.excludes;
            await apiCall('/manage/repos', { method: 'POST', body });
            setDiscoverSelections((s) => { const n = { ...s }; delete n[d.repoId]; return n; });
            await refresh();
            await loadDiscovery();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Sources</h1>
                    <p className="admin-page__sub">
                        GitHub repos and Google Drive folders registered in this tenant.
                        Use <strong>Discover</strong> for GitHub repos already on the org install, or
                        <strong> Add source</strong> for a Google Drive folder.
                    </p>
                </div>
                <div className="grant-row" style={{ gridTemplateColumns: 'repeat(2, auto)' }}>
                    <button className="btn btn--ghost" onClick={() => setShowAddSource(true)}>
                        + Add source
                    </button>
                    <button className="btn btn--primary" onClick={() => setShowDiscover((x) => !x)}>
                        {showDiscover ? 'Hide discover' : 'Discover GitHub →'}
                    </button>
                </div>
            </header>
            {showAddSource && (
                <AddRepoModal
                    onClose={() => setShowAddSource(false)}
                    onAdded={async () => { await refresh(); }}
                />
            )}

            {showDiscover && (
                <section className="discover-panel">
                    <header className="discover-panel__header">
                        <h2>Discover repos from {discover?.org.name || 'the configured org'}</h2>
                        <div className="discover-panel__actions">
                            <button
                                className="btn btn--ghost btn--small"
                                onClick={() => void loadDiscovery()}
                                disabled={discovering}
                            >
                                {discovering ? 'Refreshing…' : 'Refresh'}
                            </button>
                            {discover?.githubInstallUrl && (
                                <a
                                    className="btn btn--ghost btn--small"
                                    href={discover.githubInstallUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    Manage install on GitHub ↗
                                </a>
                            )}
                        </div>
                    </header>
                    {discoverError && (
                        <div className="admin-page__error">
                            {discoverError}{' '}
                            {discoverError.toLowerCase().includes('org config') && (
                                <>
                                    Configure org in <strong>Admin → Organization</strong> first.
                                </>
                            )}
                        </div>
                    )}
                    {discover && discover.repos.length === 0 && (
                        <div className="admin-page__empty">
                            No repos visible to this installation. Add some via{' '}
                            <a href={discover.githubInstallUrl} target="_blank" rel="noreferrer noopener">
                                GitHub install settings
                            </a>
                            .
                        </div>
                    )}
                    {discover && discover.repos.length > 0 && (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Repo</th>
                                    <th>Default branch</th>
                                    <th>Last push</th>
                                    <th>Description</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {discover.repos.map((d) => (
                                    <tr key={d.repoId}>
                                        <td>
                                            <code>{d.fullName}</code> {d.private && <span className="badge">private</span>}
                                        </td>
                                        <td>{d.defaultBranch}</td>
                                        <td>{d.pushedAt ? new Date(d.pushedAt).toLocaleDateString() : '—'}</td>
                                        <td className="discover-desc">{d.description ?? '—'}</td>
                                        <td>
                                            {d.registered ? (
                                                <span className="status-pill status-pill--active">registered</span>
                                            ) : (
                                                <div className="discover-add">
                                                    {discoverSelections[d.repoId] && (
                                                        <span className="discover-add__scope">
                                                            {discoverSelections[d.repoId].includes.length === 0
                                                                ? 'whole repo'
                                                                : `+${discoverSelections[d.repoId].includes.length} folder${discoverSelections[d.repoId].includes.length === 1 ? '' : 's'}`}
                                                            {discoverSelections[d.repoId].excludes.length > 0 && (
                                                                <>, -{discoverSelections[d.repoId].excludes.length}</>
                                                            )}
                                                        </span>
                                                    )}
                                                    <button
                                                        className="btn btn--ghost btn--small"
                                                        onClick={() =>
                                                            setPicker({
                                                                kind: 'discover',
                                                                repoId: d.repoId,
                                                                fullName: d.fullName,
                                                                includes: discoverSelections[d.repoId]?.includes ?? [],
                                                                excludes: discoverSelections[d.repoId]?.excludes ?? []
                                                            })
                                                        }
                                                        disabled={busy === d.repoId}
                                                    >
                                                        Choose folders…
                                                    </button>
                                                    <button
                                                        className="btn btn--primary btn--small"
                                                        onClick={() => void register(d)}
                                                        disabled={busy === d.repoId}
                                                    >
                                                        {busy === d.repoId ? 'Adding…' : '+ Add'}
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            )}

            {error && <div className="admin-page__error">{error}</div>}
            {notice && <div className="form-success">{notice}</div>}
            {allowedRepos === undefined && <div className="admin-page__loading">Loading…</div>}
            {allowedRepos && list.length === 0 && (
                <div className="admin-page__empty">
                    <p>No repos yet. Use <strong>Discover</strong> to add one from the org install.</p>
                </div>
            )}
            {list.length > 0 && (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Source</th>
                            <th>Kind</th>
                            <th>Default branch / folder</th>
                            <th>Last indexed</th>
                            <th>Sensitive lock</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((r) => {
                            const open = expanded === r.id;
                            const isDrive = r.sourceKind === 'gdrive';
                            return (
                                <Fragment key={r.id}>
                                    <tr className={open ? 'admin-row--expanded' : ''}>
                                        <td>
                                            <button
                                                className="btn btn--ghost btn--small"
                                                onClick={() => setExpanded(open ? null : r.id)}
                                                aria-label={open ? 'Collapse' : 'Expand'}
                                            >
                                                {open ? '▾' : '▸'}
                                            </button>
                                        </td>
                                        <td>
                                            <code>{r.id}</code>
                                        </td>
                                        <td>
                                            <span className={'badge ' + (isDrive ? 'badge--drive' : 'badge--github')}>
                                                {isDrive ? 'Drive' : 'GitHub'}
                                            </span>
                                        </td>
                                        <td>
                                            {isDrive
                                                ? (r.driveSource?.folderName ?? r.driveSource?.folderId ?? '—')
                                                : r.defaultBranch}
                                        </td>
                                        <td>{r.lastIndexedAt ? new Date(r.lastIndexedAt).toLocaleString() : '—'}</td>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={r.sensitiveContentLock}
                                                disabled={busy === r.id}
                                                onChange={(e) => void patchRepo(r, { sensitiveContentLock: e.target.checked })}
                                            />
                                        </td>
                                        <td>{r.status ?? 'active'}</td>
                                    </tr>
                                    {open && (
                                        <tr className="admin-row--detail">
                                            <td></td>
                                            <td colSpan={6}>
                                                <div className="user-detail">
                                                    {isDrive && r.driveSource && (
                                                        <>
                                                            <h4>Drive folder</h4>
                                                            <p className="form-hint">
                                                                {r.driveSource.folderId
                                                                    ? <>Folder ID: <code>{r.driveSource.folderId}</code></>
                                                                    : <>Folder details restricted.</>}
                                                                {r.driveSource.folderName && (
                                                                    <> — {r.driveSource.folderName}</>
                                                                )}
                                                            </p>
                                                            <h4>Doc-type filters</h4>
                                                            <p className="form-hint">
                                                                Re-index after changing filters to apply.
                                                            </p>
                                                            <DriveDocTypeEditor
                                                                repo={r}
                                                                busy={busy === r.id}
                                                                onSave={async (filters) => {
                                                                    await patchRepo(
                                                                        r,
                                                                        { driveDocTypeFilters: filters } as unknown as Partial<RepoFull>
                                                                    );
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                    {!isDrive && (
                                                        <>
                                                            <h4>Indexed folders</h4>
                                                            <p className="form-hint">
                                                                {(r.indexPaths ?? []).length === 0
                                                                    ? <>Currently indexing the <strong>whole repo</strong>.</>
                                                                    : <>Including <strong>{(r.indexPaths ?? []).length}</strong> folder(s).</>}
                                                                {(r.indexExcludePaths ?? []).length > 0 && (
                                                                    <> Excluding {r.indexExcludePaths?.length}.</>
                                                                )}{' '}
                                                                Click <strong>Re-index</strong> after editing.
                                                            </p>
                                                            {(r.indexPaths ?? []).length > 0 && (
                                                                <ul className="grant-list">
                                                                    {(r.indexPaths ?? []).map((p) => <li key={p}><code>+ {p}</code></li>)}
                                                                </ul>
                                                            )}
                                                            {(r.indexExcludePaths ?? []).length > 0 && (
                                                                <ul className="grant-list">
                                                                    {(r.indexExcludePaths ?? []).map((p) => <li key={p}><code>− {p}</code></li>)}
                                                                </ul>
                                                            )}
                                                            <button
                                                                className="btn btn--ghost btn--small"
                                                                onClick={() => setPicker({ kind: 'edit', repo: r })}
                                                                disabled={busy === r.id}
                                                            >
                                                                Choose folders…
                                                            </button>

                                                            <h4>Default issue labels</h4>
                                                            <p className="form-hint">
                                                                Comma-separated. Applied to every issue filed from this repo,
                                                                in addition to <code>docreview</code>, category, and{' '}
                                                                <code>submitted-by:</code> labels.
                                                            </p>
                                                            <div className="grant-row">
                                                                <input
                                                                    type="text"
                                                                    value={labelDraft[r.id] ?? (r.defaultIssueLabels ?? []).join(', ')}
                                                                    onChange={(e) =>
                                                                        setLabelDraft((d) => ({ ...d, [r.id]: e.target.value }))
                                                                    }
                                                                    placeholder="needs-triage, internal"
                                                                    disabled={busy === r.id}
                                                                />
                                                                <button
                                                                    className="btn btn--primary btn--small"
                                                                    onClick={() => saveLabels(r)}
                                                                    disabled={busy === r.id}
                                                                >
                                                                    Save labels
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                    <h4>Actions</h4>
                                                    <div className="grant-row" style={{ gridTemplateColumns: 'repeat(4, auto)' }}>
                                                        <button
                                                            className="btn btn--ghost btn--small"
                                                            disabled={busy === r.id}
                                                            onClick={() => void reindex(r)}
                                                            title="Re-run the indexer with current settings"
                                                        >
                                                            Re-index
                                                        </button>
                                                        <button
                                                            className="btn btn--ghost btn--small"
                                                            disabled={busy === r.id}
                                                            onClick={() => void removeRepo(r)}
                                                            title="Drop the registration so you can re-add"
                                                        >
                                                            Remove
                                                        </button>
                                                        <button
                                                            className="btn btn--ghost btn--small"
                                                            disabled={busy === r.id || r.status === 'archived'}
                                                            onClick={() => void patchRepo(r, { status: 'archived' })}
                                                        >
                                                            Archive
                                                        </button>
                                                        <button
                                                            className="btn btn--ghost btn--small"
                                                            disabled={busy === r.id || (r.status ?? 'active') === 'active'}
                                                            onClick={() => void patchRepo(r, { status: 'active' })}
                                                        >
                                                            Activate
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {picker && picker.kind === 'discover' && (
                <FolderPickerModal
                    repoId={picker.repoId}
                    fullName={picker.fullName}
                    initialIncludes={picker.includes}
                    initialExcludes={picker.excludes}
                    onClose={() => setPicker(null)}
                    onConfirm={(sel) => {
                        setDiscoverSelections((s) => ({ ...s, [picker.repoId]: sel }));
                        setPicker(null);
                    }}
                />
            )}
            {picker && picker.kind === 'edit' && (
                <FolderPickerModal
                    repoId={picker.repo.id}
                    fullName={picker.repo.id}
                    initialIncludes={picker.repo.indexPaths ?? []}
                    initialExcludes={picker.repo.indexExcludePaths ?? []}
                    onClose={() => setPicker(null)}
                    onConfirm={async (sel) => {
                        setPicker(null);
                        await patchRepo(picker.repo, {
                            indexPaths: sel.includes,
                            indexExcludePaths: sel.excludes
                        } as unknown as Partial<RepoFull>);
                    }}
                />
            )}
        </div>
    );
}

function DriveDocTypeEditor({
    repo,
    busy,
    onSave
}: {
    repo: RepoFull;
    busy: boolean;
    onSave: (filters: DriveDocType[]) => Promise<void>;
}) {
    // Memoize the server-derived initial state and resync the editor whenever
    // the underlying repo's filters change (e.g. after a save → refresh, or
    // when a sibling expansion mutates the repo doc). Without this, a stale
    // local state could clobber the server on the next save.
    const initial = useMemo<DriveDocType[]>(
        () =>
            repo.driveSource?.docTypeFilters?.length
                ? repo.driveSource.docTypeFilters
                : ['all'],
        [repo.driveSource?.docTypeFilters]
    );
    const [filters, setFilters] = useState<DriveDocType[]>(initial);
    useEffect(() => {
        setFilters(initial);
    }, [initial]);
    const [saving, setSaving] = useState(false);

    function toggle(t: DriveDocType) {
        setFilters((prev) => {
            if (t === 'all') return ['all'];
            const without = prev.filter((x) => x !== 'all');
            if (without.includes(t)) {
                const next = without.filter((x) => x !== t);
                return next.length === 0 ? ['all'] : next;
            }
            return [...without, t];
        });
    }

    return (
        <div className="grant-row" style={{ gridTemplateColumns: '1fr' }}>
            <label className="form-group form-group--inline">
                <input
                    type="checkbox"
                    checked={filters.includes('all')}
                    onChange={() => toggle('all')}
                    disabled={saving || busy}
                />
                <span><strong>All supported types</strong></span>
            </label>
            {DRIVE_DOC_TYPES.map((t) => (
                <label key={t.key} className="form-group form-group--inline">
                    <input
                        type="checkbox"
                        checked={filters.includes(t.key)}
                        disabled={filters.includes('all') || saving || busy}
                        onChange={() => toggle(t.key)}
                    />
                    <span>{t.label}</span>
                </label>
            ))}
            <button
                className="btn btn--primary btn--small"
                disabled={saving || busy}
                onClick={async () => {
                    setSaving(true);
                    try {
                        await onSave(filters);
                    } finally {
                        setSaving(false);
                    }
                }}
            >
                {saving ? 'Saving…' : 'Save filters'}
            </button>
        </div>
    );
}
